import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, ExternalLink, Globe, BookOpen } from 'lucide-react';
import logo from '../assets/Focus Logo.jpg'; 

const Footer = () => {
  return (
    <footer className="bg-gray-950 text-gray-400 font-sans">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Main Grid for Footer Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 items-start text-center md:text-left">
          
          {/* Column 1: Company Branding & Digital Tools */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-15 w-20 rounded-xl shadow-md overflow-hidden flex-shrink-0">
                <img
                  src={logo}
                  loading="lazy"
                  alt="Focus Inspection Technology Logo"
                  className="h-full w-full object-cover"
                />
              </div>
              <h6 className="text-white text-2xl font-bold">
                Focus Inspection Technology
              </h6>
            </div>
            <p className="text-sm leading-relaxed mb-6 max-w-sm text-gray-500">
              Delivering advanced NDT solutions with a focus on precision, compliance, and operational excellence across various industries.
            </p>


            {/* 🔥 REFINED: FocusIn360 Box (Mobile Optimized) */}
            <div className="mb-8 p-4 rounded-2xl bg-gradient-to-br from-blue-950/30 to-transparent border border-blue-900/40 w-full max-w-[320px] md:max-w-sm flex flex-col items-center md:items-start">
              <p className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                <Globe size={12} className="animate-pulse" /> Digital Ecosystem
              </p>
              <a 
                href="https://focusin360.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 text-white hover:text-blue-400 transition-all font-bold text-base group"
              >
                FocusIn360.com 
                <ExternalLink size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
              <p className="text-[11px] mt-2 text-gray-500 font-medium">
                NDT Simulators & Digital Logbooks
              </p>
            </div>

            {/* <div className="flex gap-4">
              <a href="#" aria-label="LinkedIn" className="hover:text-blue-500 transition-colors bg-gray-900 p-2 rounded-lg">
                <Linkedin size={18} />
              </a>
              <a href="#" aria-label="Twitter" className="hover:text-blue-500 transition-colors bg-gray-900 p-2 rounded-lg">
                <Twitter size={18} />
              </a>
              <a href="#" aria-label="Facebook" className="hover:text-blue-500 transition-colors bg-gray-900 p-2 rounded-lg">
                <Facebook size={18} />
              </a>
            </div> */}
          </div>

          {/* Column 2: Full Site Navigation (Merged Navbar Menus) */}
          <div className="md:col-span-1 grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-4">Main Menu</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-blue-500 transition-colors">Home</Link></li>
                <li><Link to="/services" className="hover:text-blue-500 transition-colors">Services</Link></li>
                <li><Link to="/industries" className="hover:text-blue-500 transition-colors">Industries</Link></li>
                <li><Link to="/about" className="hover:text-blue-500 transition-colors">About Us</Link></li>
                <li><Link to="/career" className="hover:text-blue-500 transition-colors">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-blue-500 transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/resources/procedures" className="hover:text-blue-500 transition-colors">Procedures</Link></li>
                <li><Link to="/resources/asme" className="hover:text-blue-500 transition-colors">ASME Standards</Link></li>
                <li><Link to="/resources/iso" className="hover:text-blue-500 transition-colors">ISO Standards</Link></li>
                <li><Link to="/resources/guidelines" className="hover:text-blue-500 transition-colors">NDT Guidelines</Link></li>
                <li><Link to="/resources/safety" className="hover:text-blue-500 transition-colors">Safety Manuals</Link></li>
                <li><Link to="/gallery" className="hover:text-blue-500 transition-colors">Gallery</Link></li>
              </ul>
            </div>
          </div>

          {/* Column 3: Contact & Legal */}
          <div className="md:col-span-1">
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-6">Get In Touch</h4>
            <div className="space-y-5 text-sm">
              <div className="flex items-start justify-center md:justify-start gap-3">
                <MapPin size={20} className="flex-shrink-0 text-blue-500 mt-1" />
                <span className="leading-snug">407, Laaman Industrial Park, Road No.12, MIDC, Wagle Estate, Thane (W), Maharashtra 400604</span>
              </div>
              <a href="mailto:focusinspectiontechnology@gmail.com" className="flex items-center justify-center md:justify-start gap-3 hover:text-blue-500 transition-colors">
                <Mail size={18} className="flex-shrink-0 text-blue-500" />
                focusinspectiontechnology@gmail.com
              </a>
              <a href="tel:+919833205915" className="flex items-center justify-center md:justify-start gap-3 hover:text-blue-500 transition-colors">
                <Phone size={18} className="flex-shrink-0 text-blue-500" />
                +91 9833205915
              </a>
              <div className="pt-4 border-t border-gray-900">
                 <Link to="/getquote" className="text-blue-500 font-bold hover:underline">Request a Quote →</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-900 mt-12 pt-8 text-center flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} Focus Inspection Technology. All Rights Reserved.
          </p>
          <div className="flex gap-4 text-[10px] text-gray-700 uppercase tracking-tighter font-bold">
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">Privacy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-gray-400 transition-colors">Terms</Link>
            <span>•</span>
            <a href="https://focusinspection-portal.web.app/" className="hover:text-teal-500 transition-colors">Client Portal</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;